/**
 * AI Recruit - UI Store Slice
 * Redux Toolkit slice for UI state management
 */

import { createSlice } from '@reduxjs/toolkit'

interface UIState {
  sidebarOpen: boolean
  theme: 'light' | 'dark'
  notifications: Notification[]
  loading: {
    [key: string]: boolean
  }
  modals: {
    [key: string]: boolean
  }
}

export interface Notification {
  id: string
  type: 'success' | 'error' | 'warning' | 'info'
  title: string
  message?: string
  timestamp: number
  read: boolean
}

const initialState: UIState = {
  sidebarOpen: true,
  theme: 'light',
  notifications: [],
  loading: {},
  modals: {},
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    setSidebarOpen: (state, action: { payload: boolean }) => {
      state.sidebarOpen = action.payload
    },
    setTheme: (state, action: { payload: 'light' | 'dark' }) => {
      state.theme = action.payload
    },
    addNotification: (state, action: { payload: Omit<Notification, 'id' | 'timestamp' | 'read'> }) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
        timestamp: Date.now(),
        read: false,
      }
      state.notifications.unshift(notification)
    },
    markNotificationRead: (state, action: { payload: string }) => {
      const notification = state.notifications.find(n => n.id === action.payload)
      if (notification) {
        notification.read = true
      }
    },
    removeNotification: (state, action: { payload: string }) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    clearNotifications: (state) => {
      state.notifications = []
    },
    setLoading: (state, action: { payload: { key: string; loading: boolean } }) => {
      state.loading[action.payload.key] = action.payload.loading
    },
    openModal: (state, action: { payload: string }) => {
      state.modals[action.payload] = true
    },
    closeModal: (state, action: { payload: string }) => {
      state.modals[action.payload] = false
    },
    closeAllModals: (state) => {
      state.modals = {}
    },
  },
})

export const {
  toggleSidebar,
  setSidebarOpen,
  setTheme,
  addNotification,
  markNotificationRead,
  removeNotification,
  clearNotifications,
  setLoading,
  openModal,
  closeModal,
  closeAllModals,
} = uiSlice.actions

export default uiSlice.reducer